import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Shield, Building2, Activity, ArrowLeft, AlertCircle, Mail, BarChart3, DollarSign, Zap, TrendingUp, Users, Settings, Gauge, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AccountManagementPanel } from "@/components/platform-admin/AccountManagementPanel";
import { ActivityMonitor } from "@/components/platform-admin/ActivityMonitor";
import { EmailDiagnostics } from "@/components/platform-admin/EmailDiagnostics";
import { PlatformAnalyticsDashboard } from "@/components/platform-admin/PlatformAnalyticsDashboard";
import { SubscriptionAnalytics } from "@/components/platform-admin/SubscriptionAnalytics";
import { UsageMetrics } from "@/components/platform-admin/UsageMetrics";
import { SystemHealthPanel } from "@/components/platform-admin/SystemHealthPanel";
import { ConversionFunnel } from "@/components/platform-admin/ConversionFunnel";
import { OrgOnboardingPanel } from "@/components/platform-admin/OrgOnboardingPanel";
import { UserManagementPanel } from "@/components/platform-admin/UserManagementPanel";
import { PlatformOperationsDashboard } from "@/components/platform-admin/PlatformOperationsDashboard";
import { toast } from "sonner";

type AdminTab = {
  value: string;
  label: string;
  description: string;
  icon: LucideIcon;
};

const adminTabs: AdminTab[] = [
  { value: "overview", label: "Overview", description: "Revenue, funnel, platform pulse", icon: BarChart3 },
  { value: "operations", label: "Traffic & Readiness", description: "Visitor flow and launch blockers", icon: Gauge },
  { value: "subscriptions", label: "Subscriptions", description: "MRR, plans, retention", icon: DollarSign },
  { value: "onboarding", label: "Onboarding", description: "New org setup quality", icon: Settings },
  { value: "users", label: "Users", description: "Accounts and access", icon: Users },
  { value: "accounts", label: "Organizations", description: "Contractor companies", icon: Building2 },
  { value: "usage", label: "Usage", description: "Feature and AI consumption", icon: TrendingUp },
  { value: "health", label: "Health", description: "Backend and service status", icon: Zap },
  { value: "activity", label: "Activity", description: "System events and audit trail", icon: Activity },
  { value: "email", label: "Email", description: "Delivery and diagnostics", icon: Mail },
];

export default function PlatformAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isPlatformOwner, setIsPlatformOwner] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        toast.error("Please log in to access this page");
        navigate('/auth');
      } else {
        checkPlatformOwnerAccess();
      }
    }
  }, [authLoading, user]);

  const checkPlatformOwnerAccess = async () => {
    try {
      if (!user) return;

      // Check if user has platform owner role (owner role with NULL org_id)
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role, org_id')
        .eq('user_id', user.id)
        .eq('role', 'owner')
        .is('org_id', null);

      if (error) throw error;

      const hasAccess = roles && roles.length > 0;
      setIsPlatformOwner(hasAccess);

      if (!hasAccess) {
        toast.error("Access Denied: Platform Owner privileges required");
        navigate('/');
      }
    } catch (error: any) {
      console.error('Access check error:', error);
      toast.error("Failed to verify access");
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-primary animate-pulse" />
          <p className="text-muted-foreground">Verifying platform owner access...</p>
        </div>
      </div>
    );
  }

  if (!isPlatformOwner) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background p-4 md:p-6">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-xl border bg-card p-5 shadow-premium-md md:p-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-secondary to-accent" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="mt-1 shrink-0 rounded-full border bg-background/80 shadow-premium"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-glow-sm">
                <Shield className="h-6 w-6" />
              </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold tracking-normal md:text-4xl">Platform Command Center</h1>
                  <Badge variant="default" className="rounded-full bg-primary px-3 py-1 text-primary-foreground">
                  Platform Owner
                </Badge>
              </div>
                <p className="max-w-3xl text-sm text-muted-foreground md:text-base">
                  Real-time control surface for accounts, traffic, revenue, system health, and launch readiness.
              </p>
            </div>
            </div>

            <div className="grid gap-2 rounded-lg border bg-background/70 p-3 text-sm shadow-premium sm:min-w-72">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Signed in</span>
                <span className="max-w-48 truncate font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Environment</span>
                <Badge variant="outline" className="rounded-full border-primary/30 text-primary">Live Ops</Badge>
              </div>
            </div>
          </div>

          {/* Context Alert */}
          <Alert className="relative mt-5 border-primary/25 bg-primary/5">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Platform Owner Access:</strong> You can manage all organizations and system settings. 
              However, you won't see org-specific data (jobs, emails, leads) unless you're also added as a member to an organization.
            </AlertDescription>
          </Alert>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="overview" className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          <TabsList className="sticky top-6 flex h-fit flex-row flex-wrap items-stretch justify-start gap-2 rounded-xl border bg-card p-2 text-card-foreground shadow-premium lg:flex-col">
            <div className="hidden px-3 py-2 lg:block">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin Surface</p>
              <p className="mt-1 text-sm text-muted-foreground">Pick a command layer.</p>
            </div>
            {adminTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="group flex min-h-12 justify-start gap-3 rounded-lg px-3 py-3 text-left data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-glow-sm lg:w-full"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground transition-colors group-data-[state=active]:bg-primary-foreground/15 group-data-[state=active]:text-primary-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="hidden min-w-0 lg:block">
                    <span className="block truncate font-semibold">{tab.label}</span>
                    <span className="block truncate text-xs text-muted-foreground group-data-[state=active]:text-primary-foreground/80">
                      {tab.description}
                    </span>
                  </span>
                  <span className="lg:hidden">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="min-w-0">
            <TabsContent value="overview" className="mt-0 space-y-6">
              <PlatformAnalyticsDashboard />
              <div className="grid lg:grid-cols-2 gap-6">
                <ConversionFunnel />
              </div>
            </TabsContent>

            <TabsContent value="operations" className="mt-0">
              <PlatformOperationsDashboard />
            </TabsContent>

            <TabsContent value="subscriptions" className="mt-0">
              <SubscriptionAnalytics />
            </TabsContent>

            <TabsContent value="onboarding" className="mt-0">
              <OrgOnboardingPanel />
            </TabsContent>

            <TabsContent value="users" className="mt-0">
              <UserManagementPanel />
            </TabsContent>

            <TabsContent value="accounts" className="mt-0">
              <AccountManagementPanel />
            </TabsContent>

            <TabsContent value="usage" className="mt-0">
              <UsageMetrics />
            </TabsContent>

            <TabsContent value="health" className="mt-0">
              <SystemHealthPanel />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <ActivityMonitor />
            </TabsContent>

            <TabsContent value="email" className="mt-0">
              <EmailDiagnostics />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
